import { useCallback, type ComponentProps } from "react";
import { hiddenUntilScrollClass, useResizeObserver, useScrollActivity } from "@tellescope/react-hooks";

import { cn } from "../../lib/utils";
import { useDialogContentContext } from "./DialogContext";

export type DialogBodyProps = ComponentProps<"div">;

const overflows = (element: HTMLDivElement) => element.scrollHeight > element.clientHeight;

/**
 * The scrolling region of `Dialog.Content`, between `Dialog.Header` and `Dialog.Footer`. When the
 * dialog reaches its maximum height only the body scrolls, keeping the header and footer in view.
 * Without a body, the whole content scrolls instead.
 */
export const DialogBody = ({ className, onScroll, ref, ...props }: DialogBodyProps) => {
  const { divided } = useDialogContentContext();
  const { isScrolling, onScroll: trackScrollActivity } = useScrollActivity();
  const { ref: observerRef, value: isOverflowing } = useResizeObserver(overflows, false);

  const attachRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef(element);
      if (typeof ref === "function") ref(element);
      else if (ref) ref.current = element;
    },
    [observerRef, ref]
  );

  return (
    <div
      ref={attachRef}
      data-slot="dialog-body"
      // A region that scrolls must be reachable from the keyboard to be scrollable without a mouse;
      // only while it overflows, so a body that fits adds no empty tab stop.
      tabIndex={isOverflowing ? 0 : undefined}
      // `relative` makes the body the containing block for positioned descendants, such as the hidden
      // inputs form controls render; otherwise they resolve against the content, stretch its scroll
      // height past the cap, and the whole dialog scrolls as well as the body.
      className={cn(
        "relative flex min-h-0 flex-col overflow-y-auto outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        divided ? "[&:not(:first-child)]:border-t" : "gap-4",
        hiddenUntilScrollClass(isScrolling),
        className
      )}
      onScroll={(event) => {
        trackScrollActivity();
        onScroll?.(event);
      }}
      {...props}
    />
  );
};
